import type { Icon } from '@phosphor-icons/react';
import { KNOWLEDGE_REVIEW_HREF } from '~/features/files/knowledge-review/review-href';
import { PRO_BUILDER_FLAG } from '~/lib/feature-flag-names';
import {
  HouseIcon,
  ChatCircleIcon,
  BrainIcon,
  BriefcaseIcon,
  FilesIcon,
  GlobeIcon,
  RobotIcon,
  TimerIcon,
  LightningIcon,
  FlowArrowIcon,
  TerminalWindowIcon,
} from '@phosphor-icons/react';

/**
 * FRONT-285 — the command-first sidebar's destination catalog. Single source
 * of truth for every rail entry: id, label, route, icon, "home" section (now
 * only read by the command palette's grouping — FRONT-2381 removed the
 * sidebar's own "More" zone, so the sidebar itself renders one flat list —
 * the person's saved order first, then any remaining ids in this catalog's
 * order — and never groups by section), the resource-count
 * key it reads (if any), whether it carries the cron re-auth amber alert, and
 * any role gate. Deliberately dependency-light (only icons) so both
 * `sidebar-store.ts` (id validation on hydrate) and
 * `mission-control-sidebar.tsx` (rendering) can import it without a circular
 * import.
 */

export type SidebarSectionId = 'workspace' | 'build';

/** Keys the sidebar can badge when something is WAITING ON THE USER.
 *
 *  FRONT-1485: these used to be inventory counts — one per resource type, badged
 *  whether or not anything needed doing. A number in a nav rail reads like unread
 *  mail, so a stock level there is a false alarm that never clears, and it drowns
 *  out the one badge that does mean something. A destination only opts in now if
 *  it can have a queue the user has to decide on (out-of-date knowledge is one:
 *  update, keep or delete).
 *
 *  `schedule` is not here: its badge is the amber "this cron is broken" alert,
 *  computed client-side from `useCronAuthStatus` (see mission-control-sidebar). */
export type SidebarAttentionKey = 'memoryReview' | 'workNeedsOk' | 'knowledgeReview';

export interface SidebarDestination {
  id: string;
  label: string;
  path: string;
  icon: Icon;
  section: SidebarSectionId;
  /** Exact-match NavLink (`end`) — only the root Inbox route needs this. */
  end?: boolean;
  attentionKey?: SidebarAttentionKey;
  /** When set, the badge is its own link to this address (the queue's own screen) instead of part of the row's link. */
  attentionHref?: string;
  /** True for destinations whose badge can flip into the amber "needs
   *  attention" style. Only Schedule (cron re-auth) uses this today, but the
   *  flag is generic so future destinations can opt in. */
  hasAlert?: boolean;
  /**
   * FRONT-1757: `'superAdmin'` currently gates no destination — the Global
   * Settings entry it used to gate was removed (customer tenants hold
   * kb:superadmin, so the surface it led to was reachable by real
   * customers). Kept rather than pruned: it is generic sidebar-gating
   * machinery, not a single-purpose flag tied to the deleted destination —
   * see architecture-and-code.md for what is expected to need it next.
   */
  gate?: 'admin' | 'superAdmin';
  /**
   * FRONT-1501: an Unleash flag name (FRONT-325 registry) this destination
   * requires — resolved generically by callers via `isFlagEnabled` below
   * (`resolveFeatureFlag` from `use-feature-flags.ts`). Named via a constant from
   * `lib/feature-flag-names.ts` (a no-import module), so this file stays
   * dependency-light (icons + that one constant). Callers resolve it with
   * fallback `false`, so a flagged destination never shows while flags load.
   * Optional — most destinations carry no flag gate.
   */
  flag?: string;
  /**
   * FRONT-3003: clicking opens the Pro Builder window (app-shell level, see
   * `features/pro-builder/pro-builder-window-host.tsx`) instead of navigating.
   * `path` stays the deep link that opens the same window.
   */
  opensProBuilderWindow?: boolean;
}

/**
 * What choosing a destination does outside the sidebar (the command palette,
 * FRONT-3003): a destination that `opensProBuilderWindow` opens the window over
 * the current page, exactly like the sidebar row; every other one navigates.
 */
export function destinationSelectAction(dest: Pick<SidebarDestination, 'opensProBuilderWindow'>): 'open-pro-builder' | 'navigate' {
  return dest.opensProBuilderWindow ? 'open-pro-builder' : 'navigate';
}

export const SIDEBAR_SECTIONS: { id: SidebarSectionId; label: string }[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'build', label: 'Build' },
];

export const SIDEBAR_DESTINATIONS: SidebarDestination[] = [
  // Workspace
  { id: 'inbox', label: 'Inbox', path: '/', icon: HouseIcon, section: 'workspace', end: true },
  { id: 'chats', label: 'Chats', path: '/chats', icon: ChatCircleIcon, section: 'workspace' },
  {
    id: 'memory',
    label: 'Memory',
    path: '/memory',
    icon: BrainIcon,
    section: 'workspace',
    attentionKey: 'memoryReview',
  },
  // FRONT-892: a curation lens over the graph's project entities — client
  // work, deals, and recurring business obligations, one board.
  {
    id: 'work',
    label: 'Projects',
    path: '/work',
    icon: BriefcaseIcon,
    section: 'workspace',
    // FRONT-1485: the "Needs your OK" commitments strip.
    attentionKey: 'workNeedsOk',
  },
  // FRONT-1460: labelled "Knowledge" — the section now covers company
  // knowledge bases as well as the user's own files and folders. The PATH
  // stays /files: renaming the URL would touch 35 call sites for no
  // user-visible gain, and /knowledge redirects here for anyone who types it.
  {
    id: 'files',
    label: 'Knowledge',
    path: '/files',
    icon: FilesIcon,
    section: 'workspace',
    // Out-of-date knowledge; the number links straight to the review dialog.
    attentionKey: 'knowledgeReview',
    attentionHref: KNOWLEDGE_REVIEW_HREF,
  },
  { id: 'apps', label: 'Apps', path: '/admin/apps', icon: GlobeIcon, section: 'workspace' },
  // Build
  { id: 'agents', label: 'Agents', path: '/agents', icon: RobotIcon, section: 'build' },
  {
    id: 'schedule',
    label: 'Schedule',
    path: '/crons',
    icon: TimerIcon,
    section: 'build',
    hasAlert: true,
  },
  { id: 'skills', label: 'Skills', path: '/skills', icon: LightningIcon, section: 'build' },
  // FRONT-1201: the platform's agentic workflows. No badge — the count comes
  // from the workflow-service, not from Eddy's own resource counts endpoint.
  { id: 'workflows', label: 'Workflows', path: '/workflows', icon: FlowArrowIcon, section: 'build' },
  // FRONT-3003: the person's own Claude Code in a terminal. Per-person flag,
  // dark release: callers resolve flagged destinations with fallback `false`
  // so the entry never shows while flags load or for a person without it.
  {
    id: 'pro-builder',
    label: 'Pro Builder',
    path: '/pro-builder',
    icon: TerminalWindowIcon,
    section: 'build',
    flag: PRO_BUILDER_FLAG,
    opensProBuilderWindow: true,
  },
  // FRONT-2376 removed Policy, Tools, Connections, Audit log, Org Settings,
  // Sandbox, Status and Diagnostics from this catalog — all eight moved into
  // the System Settings modal (`/system-settings`, `lib/system-settings-
  // sections.ts`), reached from the avatar menu and the command palette
  // rather than this sidebar. That emptied the `administration` and `health`
  // sections entirely, so both were removed from `SIDEBAR_SECTIONS` too
  // rather than left as headers with nothing under them.
];

export const SIDEBAR_DESTINATION_IDS: readonly string[] = SIDEBAR_DESTINATIONS.map((d) => d.id);

const DESTINATION_BY_ID: ReadonlyMap<string, SidebarDestination> = new Map(
  SIDEBAR_DESTINATIONS.map((d) => [d.id, d]),
);

export function getSidebarDestination(id: string): SidebarDestination | undefined {
  return DESTINATION_BY_ID.get(id);
}

export function isSidebarDestinationVisible(
  dest: SidebarDestination,
  perms: {
    isAdmin: boolean;
    isSuperAdmin: boolean;
    /**
     * FRONT-1501: resolves a `dest.flag` name to on/off. Required (not
     * optional) so a caller can't silently forget to wire flag-gating —
     * both current callers (`mission-control-sidebar.tsx`,
     * `command-palette.tsx`) already fetch `useFeatureFlags()` for other
     * reasons and can build this from `resolveFeatureFlag`. Pass `true` for
     * any flag not loaded yet, mirroring `useFeatureFlag`'s own
     * "server still gates the actual action" fail-open default — a
     * flag-gated destination should stay reachable through a flags-endpoint
     * hiccup or before its toggle exists in Unleash, not disappear.
     */
    isFlagEnabled: (flag: string) => boolean;
  },
): boolean {
  if (dest.gate === 'admin' && !perms.isAdmin) return false;
  if (dest.gate === 'superAdmin' && !perms.isSuperAdmin) return false;
  if (dest.flag && !perms.isFlagEnabled(dest.flag)) return false;
  return true;
}
